import { AlertTriangle, Save, RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { unsavedChangesManager } from '../utils/unsavedChanges';
import { useState, useEffect } from 'react';

interface SessionExpirationModalProps {
  isOpen: boolean;
  onSaveDrafts: () => void;
  onReauthenticate: () => void;
  onDiscard: () => void;
}

export function SessionExpirationModal({
  isOpen,
  onSaveDrafts,
  onReauthenticate,
  onDiscard,
}: SessionExpirationModalProps) {
  const [unsavedCount, setUnsavedCount] = useState(0);
  const [unsavedItems, setUnsavedItems] = useState<Array<{ componentId: string; componentName: string; timestamp: number }>>([]);

  useEffect(() => {
    if (isOpen) {
      const changes = unsavedChangesManager.getAllUnsavedChanges();
      setUnsavedCount(changes.length);
      setUnsavedItems(changes.map(c => ({
        componentId: c.componentId,
        componentName: c.componentName,
        timestamp: c.timestamp,
      })));
    }
  }, [isOpen]);

  const formatTimestamp = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    return `${minutes} minutes ago`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[500px]" onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-orange-100">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Session Expired</DialogTitle>
              <DialogDescription className="mt-1">
                Your session has expired, but you have unsaved changes.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Save className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 mb-1">
                  {unsavedCount} {unsavedCount === 1 ? 'Unsaved Change' : 'Unsaved Changes'}
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  Your work has been automatically saved to your browser. You can recover it after logging back in.
                </p>
                {unsavedItems.length > 0 && (
                  <div className="space-y-2">
                    {unsavedItems.map((item) => (
                      <div key={item.componentId} className="text-sm bg-white rounded px-3 py-2 border border-blue-200">
                        <div className="font-medium text-blue-900">{item.componentName}</div>
                        <div className="text-blue-600 text-xs mt-0.5">{formatTimestamp(item.timestamp)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={onReauthenticate}
              className="w-full"
              size="lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Log Back In to Continue
            </Button>

            <Button
              onClick={onSaveDrafts}
              variant="outline"
              className="w-full"
              size="lg"
            >
              <Save className="w-4 h-4 mr-2" />
              Confirm Drafts are Saved
            </Button>

            <Button
              onClick={onDiscard}
              variant="ghost"
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-2" />
              Discard Changes & Logout
            </Button>
          </div>
        </div>

        <div className="text-xs text-gray-500 text-center pt-2 border-t">
          Your drafts will be saved in this browser for 7 days
        </div>
      </DialogContent>
    </Dialog>
  );
}
